export const ADVANCED_METRICS_ACCESS_BLOCK = "advanced_metrics";
export const ADVANCED_METRICS_PERMISSION = "advanced_metrics.view";

export function toCompanyValueSet(values) {
  if (values == null || values === "") return new Set();
  const source = values instanceof Set
    ? [...values]
    : Array.isArray(values)
      ? values
      : String(values).split(",");
  return new Set(
    source
      .map((value) => String(value).trim())
      .filter(Boolean),
  );
}

export function mergeCompanyValueSets(...collections) {
  return new Set(
    collections.flatMap((collection) => [...toCompanyValueSet(collection)]),
  );
}

export const COMPANY_PERMISSION_MODULES = Object.freeze({
  inbox: {
    order: 10,
    accessBlock: "inbox",
    labelKey: "permission-matrix.modules.inbox",
    fallbackLabel: "Inbox",
  },
  campaigns: {
    order: 20,
    accessBlock: "bulk_sending",
    labelKey: "permission-matrix.modules.campaigns",
    fallbackLabel: "Campaigns",
  },
  contacts: {
    order: 30,
    labelKey: "permission-matrix.modules.contacts",
    fallbackLabel: "Contacts",
  },
  channels: {
    order: 40,
    accessBlock: "channels",
    labelKey: "permission-matrix.modules.channels",
    fallbackLabel: "Channels",
  },
  smart_agent: {
    order: 50,
    accessBlock: "smart_agent",
    labelKey: "permission-matrix.modules.smart-agent",
    fallbackLabel: "AI agents",
  },
  workflows: {
    order: 60,
    accessBlock: "workflows",
    labelKey: "permission-matrix.modules.workflows",
    fallbackLabel: "Workflows",
  },
  forms: {
    order: 65,
    accessBlock: "forms",
    labelKey: "permission-matrix.modules.forms",
    fallbackLabel: "Forms",
  },
  automations: {
    order: 70,
    accessBlock: "automations",
    labelKey: "permission-matrix.modules.automations",
    fallbackLabel: "Automations and integrations",
  },
  kanban: {
    order: 80,
    accessBlock: "kanban",
    labelKey: "permission-matrix.modules.kanban",
    fallbackLabel: "Kanban",
  },
  sales_payments: {
    order: 90,
    accessBlock: "sales_payments",
    labelKey: "permission-matrix.modules.sales-payments",
    fallbackLabel: "Sales and payments",
  },
  tracking: {
    order: 100,
    accessBlock: "automations",
    labelKey: "permission-matrix.modules.tracking",
    fallbackLabel: "Tracking",
  },
  advanced_metrics: {
    order: 110,
    accessBlock: ADVANCED_METRICS_ACCESS_BLOCK,
    labelKey: "permission-matrix.modules.advanced-metrics",
    fallbackLabel: "Advanced metrics",
  },
  company_settings: {
    order: 120,
    labelKey: "permission-matrix.modules.company-settings",
    fallbackLabel: "Company settings",
  },
  other: {
    order: 999,
    labelKey: "permission-matrix.modules.other",
    fallbackLabel: "Other permissions",
  },
});

export const COMPANY_PERMISSION_COLUMNS = Object.freeze([
  {
    key: "view",
    order: 10,
    labelKey: "permission-matrix.columns.view",
    fallbackLabel: "View",
  },
  {
    key: "write",
    order: 20,
    labelKey: "permission-matrix.columns.write",
    fallbackLabel: "Operate",
  },
  {
    key: "create",
    order: 30,
    labelKey: "permission-matrix.columns.create",
    fallbackLabel: "Create",
  },
  {
    key: "edit",
    order: 40,
    labelKey: "permission-matrix.columns.edit",
    fallbackLabel: "Edit",
  },
  {
    key: "delete",
    order: 50,
    labelKey: "permission-matrix.columns.delete",
    fallbackLabel: "Delete",
  },
  {
    key: "reports",
    order: 60,
    labelKey: "permission-matrix.columns.reports",
    fallbackLabel: "Reports",
  },
  {
    key: "manage",
    order: 70,
    labelKey: "permission-matrix.columns.manage",
    fallbackLabel: "Manage",
  },
  {
    key: "other",
    order: 80,
    labelKey: "permission-matrix.columns.other",
    fallbackLabel: "Other actions",
  },
]);

const MODULE_ALIASES = Object.freeze({
  inbox: "inbox",
  chat: "inbox",
  conversations: "inbox",
  conversation: "inbox",
  campaigns: "campaigns",
  campaign: "campaigns",
  bulk_sending: "campaigns",
  messages: "campaigns",
  contacts: "contacts",
  contact: "contacts",
  channels: "channels",
  channel: "channels",
  smart_agent: "smart_agent",
  smart_agents: "smart_agent",
  agents: "smart_agent",
  agent: "smart_agent",
  workflows: "workflows",
  workflow: "workflows",
  forms: "forms",
  form: "forms",
  automations: "automations",
  automation: "automations",
  integrations: "automations",
  integration: "automations",
  kanban: "kanban",
  sales_payments: "sales_payments",
  payments: "sales_payments",
  payment: "sales_payments",
  sales: "sales_payments",
  tracking: "tracking",
  advanced_metrics: "advanced_metrics",
  metrics: "advanced_metrics",
  company_settings: "company_settings",
  settings: "company_settings",
});

const ACTION_ALIASES = Object.freeze({
  access: "view",
  read: "view",
  view: "view",
  list: "view",
  use: "view",
  write: "write",
  operate: "write",
  send: "write",
  create: "create",
  add: "create",
  edit: "edit",
  update: "edit",
  delete: "delete",
  remove: "delete",
  report: "reports",
  reports: "reports",
  metrics: "reports",
  analytics: "reports",
  manage: "manage",
  configure: "manage",
  config: "manage",
});

const SPECIAL_ACTION_LABELS = Object.freeze({
  view_amounts: {
    column: "other",
    labelKey: "permission-matrix.actions.view-amounts",
    fallbackLabel: "View financial amounts",
  },
  review: {
    column: "other",
    labelKey: "permission-matrix.actions.review",
    fallbackLabel: "Review",
  },
  confirm: {
    column: "other",
    labelKey: "permission-matrix.actions.confirm",
    fallbackLabel: "Confirm",
  },
  cancel: {
    column: "other",
    labelKey: "permission-matrix.actions.cancel",
    fallbackLabel: "Cancel",
  },
  view_audit: {
    column: "other",
    labelKey: "permission-matrix.actions.view-audit",
    fallbackLabel: "View audit",
  },
  retry_deliveries: {
    column: "other",
    labelKey: "permission-matrix.actions.retry-deliveries",
    fallbackLabel: "Retry deliveries",
  },
  view_integration_health: {
    column: "other",
    labelKey: "permission-matrix.actions.integration-health",
    fallbackLabel: "View integration health",
  },
});

function normalizePermissionName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .replace(/_+/g, "_");
}

function humanize(value) {
  const text = String(value || "")
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "Permission";
}

function resolveModule(rawModule, normalizedName) {
  const direct = MODULE_ALIASES[rawModule];
  if (direct) return direct;

  const aliases = Object.keys(MODULE_ALIASES).sort(
    (left, right) => right.length - left.length,
  );
  const matched = aliases.find((alias) =>
    normalizedName.split(/[._]/).includes(alias),
  );
  return matched ? MODULE_ALIASES[matched] : rawModule || "other";
}

function resolveAction(rawAction, normalizedName) {
  if (SPECIAL_ACTION_LABELS[rawAction]) return rawAction;
  if (rawAction.startsWith("manage_") || rawAction.startsWith("configure_")) {
    return rawAction;
  }
  if (rawAction.startsWith("view_report") || rawAction.includes("metrics")) {
    return "reports";
  }
  if (ACTION_ALIASES[rawAction]) return ACTION_ALIASES[rawAction];

  const tokens = normalizedName.split(/[._]/);
  const token = tokens.find((item) => ACTION_ALIASES[item]);
  return token ? ACTION_ALIASES[token] : rawAction || "other";
}

function resolveColumn(action) {
  if (SPECIAL_ACTION_LABELS[action]) return SPECIAL_ACTION_LABELS[action].column;
  if (action.startsWith("manage_") || action.startsWith("configure_")) {
    return "manage";
  }
  return COMPANY_PERMISSION_COLUMNS.some((column) => column.key === action)
    ? action
    : "other";
}

export function describeCompanyPermission(permission) {
  const name = String(permission?.name || permission?.label || permission || "");
  const normalizedName = normalizePermissionName(name);
  const separatorIndex = normalizedName.indexOf(".");
  let rawModule = separatorIndex >= 0
    ? normalizedName.slice(0, separatorIndex)
    : "";
  let rawAction = separatorIndex >= 0
    ? normalizedName.slice(separatorIndex + 1)
    : "";

  if (!rawModule) {
    const tokens = normalizedName.split("_");
    const actionIndex = tokens.findIndex((token) => ACTION_ALIASES[token]);
    if (actionIndex === 0) {
      rawAction = tokens[0];
      rawModule = tokens.slice(1).join("_");
    } else if (actionIndex > 0) {
      rawModule = tokens.slice(0, actionIndex).join("_");
      rawAction = tokens.slice(actionIndex).join("_");
    } else {
      rawModule = normalizedName;
      rawAction = "other";
    }
  }

  const moduleKey = resolveModule(rawModule, normalizedName);
  const actionKey = resolveAction(rawAction, normalizedName);
  const moduleDefinition = COMPANY_PERMISSION_MODULES[moduleKey] || {
    order: 900,
    labelKey: null,
    fallbackLabel: humanize(moduleKey),
  };
  const actionDefinition = SPECIAL_ACTION_LABELS[actionKey] || {
    column: resolveColumn(actionKey),
    labelKey: `permission-matrix.actions.${actionKey.replace(/_/g, "-")}`,
    fallbackLabel: humanize(actionKey),
  };

  return {
    name,
    normalizedName,
    moduleKey,
    moduleDefinition,
    actionKey,
    column: resolveColumn(actionKey),
    actionDefinition,
  };
}

export const COMPANY_ROUTE_ACCESS = Object.freeze([
  {
    prefix: "/company/customer-payments/metrics",
    permissions: ["sales_payments.view", ADVANCED_METRICS_PERMISSION],
    accessBlock: ADVANCED_METRICS_ACCESS_BLOCK,
  },
  {
    prefix: "/company/customer-payments",
    permissions: ["sales_payments.view"],
  },
  {
    prefix: "/company/acquisition",
    permissions: ["sales_payments.view"],
    accessBlock: "sales_payments",
  },
  { prefix: "/channels/inbox", permissions: ["inbox.view"] },
  { prefix: "/company/campaigns", permissions: ["campaigns.view"] },
  { prefix: "/company/messages", permissions: ["campaigns.view"] },
  { prefix: "/company/actions", permissions: ["campaigns.view"] },
  { prefix: "/smart-agent/auto-reply", permissions: ["campaigns.view"] },
  { prefix: "/quick-reply", permissions: ["campaigns.view"] },
  { prefix: "/company/contacts", permissions: ["contacts.view"] },
  { prefix: "/channels", permissions: ["channels.view"] },
  { prefix: "/smart-agent", permissions: ["smart_agent.view"] },
  { prefix: "/calendar", permissions: ["smart_agent.view"] },
  { prefix: "/workflows", permissions: ["workflows.view"], accessBlock: "workflows" },
  { prefix: "/forms", anyPermissions: ["forms.view", "forms.manage"], accessBlock: "forms" },
  { prefix: "/company/webhook-integrations", permissions: ["automations.view"] },
  { prefix: "/company/mcp", permissions: ["automations.view"] },
  { prefix: "/company/apis", permissions: ["automations.view"] },
  {
    prefix: "/company/tracking",
    anyPermissions: ["tracking.view", "tracking.manage"],
    accessBlock: "automations",
  },
  { prefix: "/company/kanban", permissions: ["kanban.view"] },
  { prefix: "/company/funnels", permissions: ["kanban.view"] },
]);

export function getCompanyRouteAccess(pathname) {
  const path = String(pathname || "");
  return COMPANY_ROUTE_ACCESS.find((route) => path.startsWith(route.prefix)) || null;
}
export function hasCompanyFeatureAccess({
  access,
  accessBlock,
  permission,
}) {
  if (!access) return false;
  const accessBlocks = toCompanyValueSet(access.accessBlocks);
  const permissions = toCompanyValueSet(access.permissions);
  const entitled =
    !accessBlock ||
    access.isPlatformAdmin === true ||
    accessBlocks.has(accessBlock);
  const permitted =
    !permission ||
    access.isPlatformAdmin === true ||
    access.isOwner === true ||
    access.isApiActor === true ||
    permissions.has(permission);
  return entitled && permitted;
}
